import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Form,
  message,
  Button,
  Input,
  Spin,
  Switch,
  Select,
  InputNumber,
  DatePicker,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { RxSketchLogo } from "react-icons/rx";
import {
  createReward,
  getBrandsFiltered,
  getCategoriesFiltered,
  getClientLevels,
  getClientSegmentsFiltered,
  getRewardTypes,
  getClusters,
  getBranchOfficesFiltered,
} from "../../../../api/Services";

import styles from "./NewReward.module.scss";
import {
  hasPermission,
  privilegesEnum,
} from "../../../../helpers/PermissionHelper";
import AddArticleModal from "../../../../components/addarticlemodal/AddArticleModal";
import CustomBreadcrumb from "../../../../components/breadcrumb/Breadcrumb";
import { getRewardsBasePath } from "../../../../helpers/RewardsPathHelper";

const NewReward = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = getRewardsBasePath(location.pathname);

  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [rewardTypes, setRewardTypes] = useState<any[]>([]);
  const [clientSegments, setClientSegments] = useState<any[]>([]);
  const [articlesSelected, setArticlesSelected] = useState<any[]>([]);
  const [clusters, setClusters] = useState<any[]>([]);
  const [branchOffices, setBranchOffices] = useState<any[]>([]);

  const [form] = Form.useForm();

  useEffect(() => {
    if (hasPermission(privilegesEnum.WRITE_REWARDS)) {
      handleSearchBrands("");
      handleSearchCategories("");
      handleSearchLevels("");
      handleSearchClientSegments("");
      handleSearchRewardTypes("");
      handleLoadClusters();
      handleSearchBranchOffices("");
    }
  }, []);

  const handleSearchBrands = async (value: string) => {
    const response = await getBrandsFiltered(value);
    setBrands(response);
  };
  const handleSearchRewardTypes = async (value: string) => {
    const response = await getRewardTypes(1, { name: value });
    setRewardTypes(response.rewardTypes);
  };
  const handleSearchClientSegments = async (value: string) => {
    const response = await getClientSegmentsFiltered(1, { name: value });
    setClientSegments(response.clientSegments);
  };

  const handleSearchLevels = async (value: string) => {
    const response = await getClientLevels(1, { name: value });
    setLevels(response.clientLevels);
  };
  const handleSearchCategories = async (value: string) => {
    const response = await getCategoriesFiltered(value);
    setCategories(response);
  };

  const handleLoadClusters = async () => {
    const response = await getClusters(1);
    setClusters(response.clusters || []);
  };

  const handleSearchBranchOffices = async (value: string) => {
    const response = await getBranchOfficesFiltered(0, { name: value });
    setBranchOffices(response.branchOffices || []);
  };

  const handleAddArticle = (articlesToAdd: any) => {
    const newArticles: any[] = [];
    articlesToAdd.forEach((articleToAdd: any) => {
      if (
        articlesSelected.find(
          (article: any) => article.id === articleToAdd.id
        ) === undefined
      )
        newArticles.push(articleToAdd);
    });
    setArticlesSelected([...articlesSelected, ...newArticles]);
  };

  const handleRemoveArticle = (articleRemove: any) => {
    const articlesWithoutRemoved = articlesSelected.filter(
      (article) => article.id !== articleRemove.id
    );

    setArticlesSelected(articlesWithoutRemoved);
  };

  const handleCreate = async () => {
    await form.validateFields().then(async (values) => {
      setLoading(true);
      const {
        name,
        status,
        rewardType,
        value,
        pointsValue,
        description,
        validity,
        brandIds,
        categoryIds,
        clientSegmentIds,
        levelIds,
        codeType,
        prefix,
        codeLength,
        clusterIds,
        branchOfficeIds,
      } = values;

      const validityStart = validity ? validity[0].format("YYYY-MM-DD") : null;
      const validityEnd = validity ? validity[1].format("YYYY-MM-DD") : null;
      const articleList = articlesSelected.map((article) => article.id);

      let response = await createReward({
        name,
        status,
        rewardType,
        value,
        pointsValue,
        description,
        validityStart,
        validityEnd,
        brandIds,
        categoryIds,
        clientSegmentIds,
        levelIds,
        articleIds: articleList,
        articleGroupIds: [],
        codeLength,
        codeType,
        prefix,
        clusterIds: clusterIds || [],
        branchOfficeIds: branchOfficeIds || [],
      });
      if (response) {
        message.success(t("Common-CreateSuccessMessage"));
        form.resetFields();
        navigate(basePath);
      } else message.error(t("Common-ErrorMessage"));

      setLoading(false);
    });
  };

  const getOptions = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      options.push(
        <Select.Option value={item.id} key={item.id}>
          {item.name}
        </Select.Option>
      );
    });

    return options;
  };

  const getOptionsCategories = (items: any) => {
    const options: any[] = [];

    items?.forEach(function (item: any) {
      const parentsString = item?.parents
        ? item.parents.filter(Boolean).join(" - ")
        : "";
      const categoryName = item?.name || "";

      const categoryTree = parentsString
        ? `${parentsString} - ${categoryName}`
        : categoryName;

      options.push(
        <Select.Option value={item.id} key={item.id}>
          {categoryTree}
        </Select.Option>
      );
    });

    return options;
  };
  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxSketchLogo />,
                label: t("Common-Rewards"),
                path: basePath
              },
              {
                label: t("Common-NewReward"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={handleCreate}
            className={styles.saveButton}
            type="primary"
          >
            {t("Common-Add")}
          </Button>
        </div>
      </div>
      <div className={styles.contentWrapper}>
        <div className={styles.rewardInfoWrapper}>
          <Form
            name="newReward"
            layout="vertical"
            initialValues={{ remember: true }}
            form={form}
            autoComplete="off"
            className={styles.newForm}
          >
            <div className={styles.sectionTitle}>{t("Common-BasicInformation")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Name")}
                name="name"
                rules={[
                  {
                    required: true,
                    message: t("Common-NameNotEntered"),
                  },
                ]}
              >
                <Input placeholder={t("Common-Name")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Type")}
                name="rewardType"
                rules={[
                  {
                    required: true,
                    message: t("Common-Type"),
                  },
                ]}
              >
                <Select
                  allowClear
                  filterOption={(input: string, option: any) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                    0
                  }
                  onSearch={handleSearchRewardTypes}
                  placeholder={t("Common-SearchByName")}
                >
                  {getOptions(rewardTypes)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item 
                className={styles.formItem}
                label={t("Common-Description")} 
                name="description"
              >
                <Input placeholder={t("Common-Description")} />
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                initialValue={false}
                label={t("Common-Active")}
                name="status"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </div>
            <div className={styles.sectionTitle}>{t("Common-Configuration")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Value")}
                name="value"
              >
                <InputNumber
                  className={styles.inputNumber}
                  min={0}
                  placeholder={t("Common-Value")}
                />
              </Form.Item>
              <div className={styles.formItem} />
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-PointsValue")}
                name="pointsValue"
              >
                <InputNumber
                  className={styles.inputNumber}
                  min={0}
                  placeholder={t("Common-PointsValue")}
                />
              </Form.Item>
              <div className={styles.formItem}></div>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Valid")}
                name="validity"
              >
                <DatePicker.RangePicker
                  className={styles.dateSelector}
                  placeholder={[t("Common-ValidFrom"), t("Common-ValidTo")]}
                />
              </Form.Item>
              <div className={styles.formItem} />
            </div>
            <div className={styles.sectionTitle}>{t("Common-CategoriesAndSegments")}</div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Brands")}
                name="brandIds"
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t("Common-SearchByName")}
                  onSearch={handleSearchBrands}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(brands)}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Categories")}
                name="categoryIds"
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  onSearch={handleSearchCategories}
                  placeholder={t("Common-SearchByName")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ width: "100%" }}
                >
                  {getOptionsCategories(categories)}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Clusters")}
                name="clusterIds"
              >
                <Select
                  mode="multiple"
                  allowClear
                  placeholder={t("Common-Select")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {clusters?.map((c: any) => (
                    <Select.Option value={c.id} key={c.id}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-BranchOffices")}
                name="branchOfficeIds"
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  onSearch={handleSearchBranchOffices}
                  placeholder={t("Common-SearchByName")}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {branchOffices?.map((bo: any) => (
                    <Select.Option value={bo.id} key={bo.id}>
                      {bo.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className={styles.itemWrapper}>
              <Form.Item
                className={styles.formItem}
                label={t("Common-ClientSegments")}
                name="clientSegmentIds"
              >
                <Select
                  mode="multiple"
                  allowClear
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onSearch={handleSearchClientSegments}
                  placeholder={t("Common-SearchByName")}
                >
                  {getOptions(clientSegments)}
                </Select>
              </Form.Item>
              <Form.Item
                className={styles.formItem}
                label={t("Common-Level")}
                name="levelIds"
              >
                <Select
                  allowClear
                  mode="multiple"
                  showSearch
                  placeholder={t("Common-SearchByName")}
                  onSearch={handleSearchLevels}
                  filterOption={(input: string, option: any) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {getOptions(levels)}
                </Select>
              </Form.Item>
            </div>

            <AddArticleModal
              selectedArticles={articlesSelected}
              onAddArticle={handleAddArticle}
              onRemoveArticle={handleRemoveArticle}
            />
          </Form>
        </div>
      </div>
    </div>
  );
};

export default NewReward;
